import { inject, injectable } from "inversify";
import { ApiPage } from "@/core/models/page.model";
import { AddressRepo } from "../address.repo";
import { TableRepo } from "../table.repo";
import { WaterRegistrationModel } from "@/core/models/water-registration/water-registration.model";
import dayjs from "dayjs";

interface _WaterRegistrationGetParams {
    search?: string
    status?: string
    unitTypeId?: string
    provinceId?: string
    districtId?: string
    wardId?: string
    createdFrom?: string
    createdTo?: string
}

@injectable()
export class WaterRegistrationRepo extends TableRepo<WaterRegistrationModel> {
    @inject(AddressRepo)
    private addressRepo!: AddressRepo;

    constructor() {
        super("/api/UseWaterRegister");
    }

    async getData(index: number, params: _WaterRegistrationGetParams, pageSize = 10): Promise<ApiPage<WaterRegistrationModel>|null> {
        const result = await super.getData(index, params, pageSize)
        if (result) {
            const data: WaterRegistrationModel[] = [];
            for (const record of result.data) {
                const [province, district, ward] = await Promise.all([
                    this.addressRepo.getProvince(record.provinceId),
                    this.addressRepo.getDistrict(record.districtId),
                    this.addressRepo.getWard(record.wardId),
                ])
                const populatedRecord: WaterRegistrationModel = {
                    ...record,
                    provinceName: province?.name,
                    districtName: district?.name,
                    wardName: ward?.name,
                    createdAt: dayjs(record.createdAt).format("DD/MM/YYYY"),
                    updatedAt: dayjs(record.updatedAt).format("DD/MM/YYYY"),
                    identificationDate: dayjs(record.identificationDate).format("DD/MM/YYYY")
                };
                data.push(populatedRecord);
            }
            result.data = data;
            return result;
        }
        return null;
    }

    async updateStatus(id: string, status: string): Promise<boolean> {
        try {
            const response = await this.apiRepo.post(
                `${this.basepath}/${this.updatePath}`,
                { id, status }
            );
            if (response?.code === "00") {
                this.changes.next(Date.now());
                return true;
            }
            return false;
        } catch (err) {
            console.log(err);
            return false;
        }
    }

    async getDetail(id: string): Promise<WaterRegistrationModel | null> {
        try {
            const response = await this.apiRepo.get<WaterRegistrationModel>(
                `${this.basepath}/${this.detailPath}`,
                { id: id }
            );
            if (response?.code === "00" && response.result) {
                const record = response.result;
                const [province, district, ward] = await Promise.all([
                    this.addressRepo.getProvince(record.provinceId),
                    this.addressRepo.getDistrict(record.districtId),
                    this.addressRepo.getWard(record.wardId),
                ]);
                return {
                    ...record,
                    provinceName: province?.name,
                    districtName: district?.name,
                    wardName: ward?.name,
                    createdAt: dayjs(record.createdAt).format("DD/MM/YYYY"),
                    updatedAt: dayjs(record.updatedAt).format("DD/MM/YYYY"),
                    identificationDate: record.identificationDate 
                        ? dayjs(record.identificationDate).format("DD/MM/YYYY")
                        : undefined
                };
            }
            return null;
        } catch (err) {
            console.log(err);
            return null;
        }
    }
}